import type { Id } from "@convex/_generated/dataModel";

type PrinterId = Id<"printers">;

import { useMutation } from "@tanstack/react-query";
import { DollarSign } from "lucide-react";
import { useForm } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";

import {
  type LocationById,
  locationReceiptSettingsUpdateCustomerReceiptSettings,
  type TipsSettings,
} from "~/api-refs";
import { convexClient } from "~/integrations/convex/provider";
import { ProductEvents } from "~/lib/posthog";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { CustomerReceiptPreview } from "./customer-receipt-preview";

export interface CustomerReceiptSettings {
  showOrderName: boolean;
  showServerName: boolean;
  showSeatNumbers: boolean;
  printerId: PrinterId | null;
  customText: string | null;
  showModifiersBreakdown: boolean;
  showLocationNameAndLogo: boolean;
  indicateCustomerOrOrganizationCopy: boolean;
  emailSenderName: string | null;
  showSuggestedTipAmounts: boolean;
}

export function CustomerReceiptSettingsForm({
  settings,
  location,
  tipsSettings,
}: {
  settings: CustomerReceiptSettings | null;
  location: LocationById;
  tipsSettings: Pick<TipsSettings, "tipMethod" | "suggestedAmounts">;
}) {
  const updateSettingsMutation = useMutation({
    mutationFn: (data: {
      locationId: Id<"locations">;
      value: {
        showOrderName: boolean;
        showServerName: boolean;
        showSeatNumbers: boolean;
        printerId?: Id<"printers">;
        customText?: string;
        showModifiersBreakdown: boolean;
        showLocationNameAndLogo: boolean;
        indicateCustomerOrOrganizationCopy: boolean;
        emailSenderName?: string;
        showSuggestedTipAmounts: boolean;
      };
    }) =>
      convexClient.mutation(
        locationReceiptSettingsUpdateCustomerReceiptSettings,
        data
      ),
    onSuccess: () => {
      ProductEvents.settingsUpdated("customer-receipt");
    },
  });

  const form = useForm<CustomerReceiptSettings>({
    defaultValues: settings ?? {
      showOrderName: true,
      showServerName: true,
      showSeatNumbers: true,
      printerId: null,
      customText: null,
      showModifiersBreakdown: true,
      showLocationNameAndLogo: true,
      indicateCustomerOrOrganizationCopy: true,
      emailSenderName: null,
      showSuggestedTipAmounts: true,
    },
  });

  const [
    showSeatNumbers,
    showOrderName,
    showServerName,
    showModifiersBreakdown,
    showLocationNameAndLogo,
    customText,
    indicateCustomerOrOrganizationCopy,
  ] = form.watch([
    "showSeatNumbers",
    "showOrderName",
    "showServerName",
    "showModifiersBreakdown",
    "showLocationNameAndLogo",
    "customText",
    "indicateCustomerOrOrganizationCopy",
  ]);

  const update = useDebouncedCallback(() => {
    const formValues = form.getValues();
    updateSettingsMutation.mutate({
      locationId: location._id,
      value: {
        ...formValues,
        printerId: formValues.printerId ?? undefined,
        customText: formValues.customText ?? undefined,
        emailSenderName: formValues.emailSenderName ?? undefined,
      },
    });
  }, 1000);

  return (
    <div className="flex items-start gap-6">
      <CustomerReceiptPreview
        customText={customText}
        indicateCustomerOrOrganizationCopy={indicateCustomerOrOrganizationCopy}
        location={location}
        showLocationNameAndLogo={showLocationNameAndLogo}
        showModifiersBreakdown={showModifiersBreakdown}
        showOrderName={showOrderName}
        showSeatNumbers={showSeatNumbers}
        showServerName={showServerName}
        tipsSettings={tipsSettings}
      />
      <div className="w-full flex-1">
        <Form {...form}>
          <form className="space-y-4">
            <div>
              <h3 className="subtitle-sm text-foreground py-5 text-balance">
                Receipt Information
              </h3>
              <hr className="bg-border mb-4 h-px border-none" />
            </div>
            <div className="bg-accent flex gap-2 rounded-xl p-4">
              <DollarSign className="text-foreground size-5" />
              <div>
                <p className="label text-foreground">
                  Tip Method:{" "}
                  {
                    {
                      disabled: "Disabled",
                      "in-pos": "on POS screen",
                      "on-receipt": "on paper receipt",
                    }[tipsSettings.tipMethod]
                  }
                </p>
                <p className="body-sm text-muted-foreground">
                  Configure tip method in{" "}
                  <a className="underline" href="../tips">
                    Tips
                  </a>{" "}
                  settings
                </p>
              </div>
            </div>
            <FormField
              control={form.control}
              name="showServerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show server name
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showOrderName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show order name
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showSeatNumbers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show seat numbers
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showModifiersBreakdown"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show modifiers breakdown
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="indicateCustomerOrOrganizationCopy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Indicate receipt is customer or merchant copy
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div>
              <h3 className="subtitle-sm text-foreground py-5 text-balance">
                Footer Branding
              </h3>
              <hr className="bg-border mb-4 h-px border-none" />
            </div>
            <FormField
              control={form.control}
              name="showLocationNameAndLogo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    <div className="font-medium">
                      Show location name and logo
                      <FormDescription className="body-sm text-muted-foreground">
                        Edit location details in{" "}
                        <a className="underline" href="../profile">
                          Profile
                        </a>{" "}
                        settings. Printed logos are converted to black and
                        white.
                      </FormDescription>
                    </div>
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="customText"
              render={({ field }) => (
                <>
                  <FormItem>
                    <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                      <FormControl>
                        <Switch
                          checked={field.value !== null}
                          onCheckedChange={(v) => {
                            field.onChange(v ? "" : null);
                            update();
                          }}
                        />
                      </FormControl>
                      Show custom text
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                  {field.value !== null && (
                    <FormItem>
                      <FormLabel>Custom footer text</FormLabel>
                      <FormControl>
                        <textarea
                          className="border-input bg-background text-foreground field-sizing-content w-full max-w-2xl rounded-md border px-3 py-2"
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            update();
                          }}
                          value={field.value}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                </>
              )}
            />
            <div>
              <h3 className="subtitle-sm text-foreground py-5 text-balance">
                Digital Receipts
              </h3>
              <hr className="bg-border mb-4 h-px border-none" />
            </div>
            <FormField
              control={form.control}
              name="emailSenderName"
              render={({ field }) => (
                <>
                  <FormItem>
                    <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                      <FormControl>
                        <Switch
                          checked={field.value !== null}
                          onCheckedChange={(v) => {
                            field.onChange(v ? "" : null);
                            update();
                          }}
                        />
                      </FormControl>
                      <div className="font-medium">
                        Send email receipts to customers
                        <FormDescription className="body-sm text-muted-foreground">
                          Customers will be asked to enter their email address
                          to receive digital receipts
                        </FormDescription>
                      </div>
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                  {field.value !== null && (
                    <FormItem>
                      <FormLabel>Customise email sender name</FormLabel>
                      <FormControl>
                        <Input
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            update();
                          }}
                          value={field.value}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                </>
              )}
            />
          </form>
        </Form>
      </div>
    </div>
  );
}
